import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import {
  type LazyComponent,
  lazyComponent,
  type ModulePick,
  type RouteLoader,
} from '~/routes/lazyRoute';

import { type ModalProps, ModalType } from '../Modal/interface';

/**
 * Modal modules that get their own JS/CSS chunk.
 *
 * As with the route manifest, the glob keys double as the module ids Vite
 * writes into the SSR manifest, so a loader and the manifest entry the server
 * looks its stylesheet up by can never drift apart.
 */
const MODAL_MODULES = import.meta.glob([
  '/src/components/DeliveryPickupAddressModal/DeliveryPickupAddressModal.tsx',
  '/src/components/Modal3DS/Modal3DS.tsx',
  '/src/components/ModalAddressListV3/ModalAddressList.tsx',
  '/src/components/ModalAgeRestricted/ModalAgeRestricted.tsx',
  '/src/components/ModalAuthHeimV3/ModalAuthHeimV3.tsx',
  '/src/components/ModalAuthV3/ModalAuthV3.tsx',
  '/src/components/ModalBirthDate/ModalBirthDate.tsx',
  '/src/components/ModalChangeEmailV3/ModalChangeEmailV3.tsx',
  '/src/components/ModalChangeName/ModalChangeName.tsx',
  '/src/components/ModalChangeNotifications/ModalChangeNotifications.tsx',
  '/src/components/ModalCheckoutDestination/ModalCheckoutDestination.tsx',
  '/src/components/ModalCheckoutRecieverCD/ModalCheckoutRecieverCD.tsx',
  '/src/components/ModalComment/ModalComment.tsx',
  '/src/components/ModalCreateReviewV3/ModalCreateReviewV3.tsx',
  '/src/components/ModalDeleteAccount/ModalDeleteAccount.tsx',
  '/src/components/ModalDeleteAddress/ModalDeleteAddress.tsx',
  '/src/components/ModalDeleteCard/ModalDeleteCard.tsx',
  '/src/components/ModalDeliveryAddress/ModalDeliveryAddress.tsx',
  '/src/components/ModalDeliveryInfoCDV3/ModalDeliveryInfoCDV3.tsx',
  '/src/components/ModalDeliveryMethodChange/ModalDeliveryMethodChange.tsx',
  '/src/components/ModalEditAddressDetailsV3/ModalEditAddressDetailsV3.tsx',
  '/src/components/ModalEditAddressLocationV3/ModalEditAddressLocationV3.tsx',
  '/src/components/ModalEditAddressV3/ModalEditAddressV3.tsx',
  '/src/components/ModalFeedback/ModalFeedback.tsx',
  '/src/components/ModalFeedback/ModalFeedbackSuccess.tsx',
  '/src/components/ModalGeofencing/ModalGeofencing.tsx',
  '/src/components/ModalLogoutV3/ModalLogoutV3.tsx',
  '/src/components/ModalOutOfZone/ModalOutOfZone.tsx',
  '/src/components/ModalProductDetails/ModalProductDetails.tsx',
  '/src/components/ModalPromocode/ModalPromocode.tsx',
  '/src/components/ModalRateApp/ModalRateApp.tsx',
  '/src/components/ModalRecommendations/ModalRecommendations.tsx',
  '/src/components/ModalSelectProductSKUV3/ModalSelectProductSKU.tsx',
  '/src/components/ModalSlotsSelectDate/ModalSlotsSelectDate.tsx',
  '/src/components/ModalSlotsSelectDateV3/ModalSlotsSelectDateV3.tsx',
  '/src/components/ModalSubscription/ModalSubscription.tsx',
  '/src/components/ModalTransactionDetails/ModalTransactionDetails.tsx',
  '/src/pages/PaymentMethod/components/ModalBindCard/ModalBindCard.tsx',
  '/src/pages/PaymentMethod/PaymentMethod.tsx',
  '/src/pages/ReferralProgram/ReferralProgramModal.tsx',
]);

type StatusColor = 'dark-content' | 'light-content';
type ModalOwnProps = Pick<ModalProps, 'isShow'>;

interface ModalMapItem {
  statusColor: StatusColor;
  /** Module id as it appears in Vite's SSR manifest. */
  moduleId: string;
  component: LazyComponent<ModalOwnProps>;
}

/**
 * A value that may depend on the company, and so must be read per request
 * rather than when this module is evaluated.
 */
type PerCompany<T> = T | (() => T);

const resolve = <T>(value: PerCompany<T>): T =>
  typeof value === 'function' ? (value as () => T)() : value;

interface ModalEntry {
  statusColor: PerCompany<StatusColor>;
  moduleId: PerCompany<string>;
  pick?: ModulePick<ModalOwnProps>;
}

function loaderFor(moduleId: string): RouteLoader {
  const loader = MODAL_MODULES[`/${moduleId}`];
  if (!loader) {
    throw new Error(
      `modalMap: "${moduleId}" is missing from the modal module glob`,
    );
  }
  return loader as RouteLoader;
}

const pickNamed =
  (name: string): ModulePick<ModalOwnProps> =>
  (module) =>
    module[name] as never;

const map = new Map<ModalType, ModalEntry>();

/**
 * One component per module, created on first use and kept, so a modal keeps its
 * identity across renders instead of remounting.
 */
const components = new Map<string, LazyComponent<ModalOwnProps>>();

function componentFor(
  moduleId: string,
  pick?: ModulePick<ModalOwnProps>,
): LazyComponent<ModalOwnProps> {
  let component = components.get(moduleId);
  if (!component) {
    // No `reloadToRecover`: a modal whose chunk fails is caught by
    // `ModalErrorBoundary` and reported in place, which keeps whatever the page
    // behind it still holds.
    component = lazyComponent<ModalOwnProps>(moduleId, loaderFor(moduleId), {
      pick,
    });
    components.set(moduleId, component);
  }
  return component;
}

/**
 * Registers a modal module as its own chunk.
 *
 * `moduleId` and `statusColor` accept a function for the modals that differ by
 * brand. Reading `company` here instead would freeze the choice: this module is
 * evaluated once, and on the server that is at startup, before any request has
 * set the company — so every brand would be served the default brand's modal.
 */
function define(
  types: ModalType[],
  moduleId: PerCompany<string>,
  statusColor: PerCompany<StatusColor>,
  pick?: ModulePick<ModalOwnProps>,
) {
  for (const type of types) {
    map.set(type, { moduleId, statusColor, pick });
  }
}

/**
 * Resolves a modal for the company of the request being handled.
 */
export function getModal(modalType: ModalType): ModalMapItem | undefined {
  const entry = map.get(modalType);
  if (!entry) {
    return undefined;
  }

  const moduleId = resolve(entry.moduleId);
  return {
    moduleId,
    statusColor: resolve(entry.statusColor),
    component: componentFor(moduleId, entry.pick),
  };
}

const AUTH_MODULE = () =>
  company.match({
    [CompanyName.Heimkaup]:
      'src/components/ModalAuthHeimV3/ModalAuthHeimV3.tsx',
    default: 'src/components/ModalAuthV3/ModalAuthV3.tsx',
  });

/** Address flow without a location-name field, see `usesDeliveryPickupAddressModal`. */
const DELIVERY_PICKUP_ADDRESS_MODULE =
  'src/components/DeliveryPickupAddressModal/DeliveryPickupAddressModal.tsx';

const EDIT_ADDRESS_DETAILS_MODULE = () =>
  company.usesDeliveryPickupAddressModal
    ? DELIVERY_PICKUP_ADDRESS_MODULE
    : company.match({
        [CompanyName.DubaiExpress]:
          'src/components/ModalEditAddressDetailsV3/ModalEditAddressDetailsV3.tsx',
        [CompanyName.DubaiExpressCom]:
          'src/components/ModalEditAddressDetailsV3/ModalEditAddressDetailsV3.tsx',
        [CompanyName.MCosmetic]:
          'src/components/ModalEditAddressDetailsV3/ModalEditAddressDetailsV3.tsx',
        default: 'src/components/ModalEditAddressV3/ModalEditAddressV3.tsx',
      });

/** Only MCosmetic splits the location step off into its own modal. */
const EDIT_ADDRESS_LOCATION_MODULE = () =>
  company.usesDeliveryPickupAddressModal
    ? DELIVERY_PICKUP_ADDRESS_MODULE
    : company.match({
        [CompanyName.MCosmetic]:
          'src/components/ModalEditAddressLocationV3/ModalEditAddressLocationV3.tsx',
        default: 'src/components/ModalEditAddressV3/ModalEditAddressV3.tsx',
      });

const ADDRESS_FLOW_STATUS_COLOR = (): StatusColor =>
  company.match({
    [CompanyName.MCosmetic]: 'dark-content',
    [CompanyName.Cerise]: 'dark-content',
    [CompanyName.CircleK]: 'dark-content',
    [CompanyName.Electronium]: 'dark-content',
    [CompanyName.Ocado]: 'dark-content',
    default: 'light-content',
  });

define([ModalType.Auth], AUTH_MODULE, 'dark-content');
define(
  [ModalType.EditAddressDetails],
  EDIT_ADDRESS_DETAILS_MODULE,
  ADDRESS_FLOW_STATUS_COLOR,
);
define(
  [ModalType.EditAddressLocation],
  EDIT_ADDRESS_LOCATION_MODULE,
  ADDRESS_FLOW_STATUS_COLOR,
);
define(
  [ModalType.AgeRestriction],
  'src/components/ModalAgeRestricted/ModalAgeRestricted.tsx',
  'light-content',
);
define(
  [ModalType.BirthDate],
  'src/components/ModalBirthDate/ModalBirthDate.tsx',
  'dark-content',
);
define(
  [ModalType.ChangeEmail],
  'src/components/ModalChangeEmailV3/ModalChangeEmailV3.tsx',
  'dark-content',
);
define(
  [ModalType.ChangeName],
  'src/components/ModalChangeName/ModalChangeName.tsx',
  'dark-content',
);
define(
  [ModalType.ChangeNotifications],
  'src/components/ModalChangeNotifications/ModalChangeNotifications.tsx',
  'dark-content',
);
define(
  [ModalType.CheckoutReciever],
  'src/components/ModalCheckoutRecieverCD/ModalCheckoutRecieverCD.tsx',
  'dark-content',
);
define(
  [ModalType.CheckoutAddRecieverData],
  'src/components/ModalCheckoutRecieverCD/ModalCheckoutRecieverCD.tsx',
  'light-content',
);
define(
  [ModalType.Comment],
  'src/components/ModalComment/ModalComment.tsx',
  'light-content',
);
define(
  [ModalType.DeleteAccount],
  'src/components/ModalDeleteAccount/ModalDeleteAccount.tsx',
  'dark-content',
);
define(
  [ModalType.DeleteAddress],
  'src/components/ModalDeleteAddress/ModalDeleteAddress.tsx',
  'dark-content',
);
define(
  [ModalType.DeleteCard],
  'src/components/ModalDeleteCard/ModalDeleteCard.tsx',
  'dark-content',
);
define(
  [ModalType.DeliveryAddress],
  'src/components/ModalDeliveryAddress/ModalDeliveryAddress.tsx',
  'dark-content',
);
define(
  [ModalType.DeliveryInfo],
  'src/components/ModalDeliveryInfoCDV3/ModalDeliveryInfoCDV3.tsx',
  'light-content',
);
define(
  [ModalType.Feedback],
  'src/components/ModalFeedback/ModalFeedback.tsx',
  'dark-content',
);
define(
  [ModalType.FeedbackSuccess],
  'src/components/ModalFeedback/ModalFeedbackSuccess.tsx',
  'dark-content',
);
define(
  [ModalType.CreateReview],
  'src/components/ModalCreateReviewV3/ModalCreateReviewV3.tsx',
  'dark-content',
);
define(
  [ModalType.Geofencing],
  'src/components/ModalGeofencing/ModalGeofencing.tsx',
  'light-content',
);
define(
  [ModalType.DeliveryMethodChange],
  'src/components/ModalDeliveryMethodChange/ModalDeliveryMethodChange.tsx',
  'light-content',
);
define(
  [ModalType.Logout],
  'src/components/ModalLogoutV3/ModalLogoutV3.tsx',
  'dark-content',
);
define(
  [ModalType.OutOfZone],
  'src/components/ModalOutOfZone/ModalOutOfZone.tsx',
  'light-content',
  pickNamed('ModalOutOfZone'),
);
define(
  [ModalType.ProductDetails],
  'src/components/ModalProductDetails/ModalProductDetails.tsx',
  'light-content',
);
define(
  [ModalType.Promocode],
  'src/components/ModalPromocode/ModalPromocode.tsx',
  'dark-content',
);
define(
  [ModalType.RateApp],
  'src/components/ModalRateApp/ModalRateApp.tsx',
  'dark-content',
);
define(
  [ModalType.Recommendations],
  'src/components/ModalRecommendations/ModalRecommendations.tsx',
  'dark-content',
);
define(
  [ModalType.ReferralProgram],
  'src/pages/ReferralProgram/ReferralProgramModal.tsx',
  'dark-content',
  pickNamed('ReferralProgramModal'),
);
define(
  [ModalType.SelectDate],
  'src/components/ModalSlotsSelectDate/ModalSlotsSelectDate.tsx',
  'dark-content',
);
define(
  [ModalType.SelectDateCD],
  'src/components/ModalSlotsSelectDateV3/ModalSlotsSelectDateV3.tsx',
  'light-content',
);
define(
  [ModalType.Subscription, ModalType.SubscriptionOff],
  'src/components/ModalSubscription/ModalSubscription.tsx',
  'dark-content',
);
define(
  [ModalType.DeliveryAddressList],
  'src/components/ModalAddressListV3/ModalAddressList.tsx',
  'light-content',
);
define(
  [ModalType.CheckoutDestination],
  'src/components/ModalCheckoutDestination/ModalCheckoutDestination.tsx',
  'light-content',
);
define(
  [ModalType.Check3Ds],
  'src/components/Modal3DS/Modal3DS.tsx',
  'dark-content',
);
define(
  [ModalType.SelectSKU],
  'src/components/ModalSelectProductSKUV3/ModalSelectProductSKU.tsx',
  'light-content',
);
if (company.is('mcosmetic')) {
  define(
    [ModalType.AddCard],
    'src/pages/PaymentMethod/components/ModalBindCard/ModalBindCard.tsx',
    'dark-content',
  );
} else {
  define(
    [ModalType.AddCard],
    'src/pages/PaymentMethod/PaymentMethod.tsx',
    'dark-content',
    pickNamed('AddPaymentMethod'),
  );
}
define(
  [ModalType.TransactionDetails],
  'src/components/ModalTransactionDetails/ModalTransactionDetails.tsx',
  'dark-content',
);

/**
 * Loads the chunk for a `?modal=` deep link and reports the module id that
 * matched, so the caller can look its assets up in the SSR manifest.
 *
 * Without this the server would render the Suspense fallback instead of the
 * modal, and the browser would only discover its stylesheet after hydration.
 */
export async function preloadModal(
  modalType: Nullable<string>,
): Promise<string[]> {
  const modal = modalType ? getModal(modalType as ModalType) : undefined;

  if (!modal) {
    return [];
  }

  await modal.component.preload();

  return [modal.moduleId];
}
